import Button from "@components/Button"
import Layout from "@components/Layout"
import { getMainGoalFromLocalStorage } from "@helpers/localStorage"
import { useTranslation } from "react-i18next"
import { Form, useSubmit } from "react-router-dom"

interface BenefitCard {
  emoji: string
  textKey: string
}

const GOAL_BENEFITS: Record<string, BenefitCard[]> = {
  loseWeight: [
    {
      emoji: "💪",
      textKey: "pages.onboarding.lookForwardTo.benefits.confidence",
    },
    {
      emoji: "⚖️",
      textKey: "pages.onboarding.lookForwardTo.benefits.reachGoalWeight",
    },
    {
      emoji: "⏱️",
      textKey: "pages.onboarding.lookForwardTo.benefits.higherSuccess",
    },
  ],
  maintainWeight: [
    {
      emoji: "💖",
      textKey: "pages.onboarding.lookForwardTo.benefits.confidence",
    },
    {
      emoji: "⚖️",
      textKey: "pages.onboarding.lookForwardTo.benefits.maintainEffortlessly",
    },
    {
      emoji: "⏱️",
      textKey: "pages.onboarding.lookForwardTo.benefits.higherSuccess",
    },
  ],
  gainWeight: [
    {
      emoji: "🏋️",
      textKey: "pages.onboarding.lookForwardTo.benefits.buildStrength",
    },
    {
      emoji: "⚡",
      textKey: "pages.onboarding.lookForwardTo.benefits.moreEnergy",
    },
    {
      emoji: "⏱️",
      textKey: "pages.onboarding.lookForwardTo.benefits.higherSuccess",
    },
  ],
  buildMuscle: [
    {
      emoji: "🏋️",
      textKey: "pages.onboarding.lookForwardTo.benefits.buildStrength",
    },
    {
      emoji: "📊",
      textKey: "pages.onboarding.lookForwardTo.benefits.optimizedNutrition",
    },
    {
      emoji: "⏱️",
      textKey: "pages.onboarding.lookForwardTo.benefits.higherSuccess",
    },
  ],
  reduceBodyFat: [
    {
      emoji: "🔥",
      textKey: "pages.onboarding.lookForwardTo.benefits.burnFat",
    },
    {
      emoji: "📊",
      textKey: "pages.onboarding.lookForwardTo.benefits.optimizedNutrition",
    },
    {
      emoji: "⏱️",
      textKey: "pages.onboarding.lookForwardTo.benefits.higherSuccess",
    },
  ],
  somethingElse: [
    {
      emoji: "💖",
      textKey: "pages.onboarding.lookForwardTo.benefits.confidence",
    },
    {
      emoji: "🍽️",
      textKey: "pages.onboarding.lookForwardTo.benefits.smarterEating",
    },
    {
      emoji: "⏱️",
      textKey: "pages.onboarding.lookForwardTo.benefits.higherSuccess",
    },
  ],
}

export default function LookForwardTo() {
  const { t } = useTranslation()
  const submit = useSubmit()
  const mainGoal = getMainGoalFromLocalStorage() ?? "loseWeight"

  const benefits = GOAL_BENEFITS[mainGoal] ?? GOAL_BENEFITS.somethingElse

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submit(event.currentTarget)
  }

  return (
    <Layout>
      <div className="flex grow flex-col items-center justify-center">
        <div className="mb-8 text-6xl">🎉</div>

        <h1 className="custom-heading mb-10">
          {t("pages.onboarding.lookForwardTo.heading")}
        </h1>

        <div className="w-full space-y-4">
          {benefits.map((benefit, index) => (
            <div
              key={benefit.textKey}
              className="flex items-center gap-4 rounded-2xl bg-white px-5 py-5"
              style={{
                animation: `fadeSlideUp 0.4s ease-out ${index * 0.15}s both`,
              }}
            >
              <div className="bg-custom-teal-light flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-2xl">
                {benefit.emoji}
              </div>
              <p className="text-base font-semibold">{t(benefit.textKey)}</p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="sticky bottom-0 -mx-5 bg-gradient-to-t from-white from-70% to-transparent px-5 pt-8 pb-4">
        <Form method="post" onSubmit={handleSubmit}>
          <input name="lookForwardTo" type="hidden" value="continue" />
          <Button
            title={t("pages.onboarding.lookForwardTo.button")}
            type="submit"
          />
        </Form>
      </div>
    </Layout>
  )
}
